/** 日本語. キー構造は en.js に従う。 */
const ja = {
    nav: {
        home: 'リサイズ',
        howItWorks: '仕組み',
        credits: 'クレジット',
        paper: '論文',
        language: '言語',
        stats: '統計',
    },

    tutorial: {
        badge: '初めての方へ',
        title: '初めてお越しのようですね',
        body:
            'PixelZoom は汎用の画像リサイザーではありません。ドット絵が乗っているブロック格子を検出し、' +
            'ドット一つひとつが正確な正方形のまま残るように拡大・縮小します。どんな画像で機能するのか、' +
            'なぜ機能しないことがあるのか、2 分だけご覧ください。',
        cta: '仕組みを見る',
        dismiss: 'いいえ、結構です',
    },

    main: {
        tagline: '小数倍率でもドット格子を崩さないドット絵リサイズ。',
        upload: {
            title: 'ここにドット絵をドロップ',
            or: 'またはクリックしてファイルを選択',
            hint: 'PNG · GIF · JPG · 最大 5 MB',
            dropNow: '離すとアップロードします',
        },
        loading: 'ブロック構造を解析中',
        error: {
            title: '画像を解析できませんでした',
            retry: '別の画像で試す',
            unreadable: '画像として読み取れないファイルです。PNG・GIF・JPG をお使いください。',
            tooLarge: '画像が大きすぎます。上限は 5 MB、1600 万画素です。',
            network: 'サーバーに接続できませんでした。接続を確認してもう一度お試しください。',
            server: 'サーバーが画像を処理できませんでした (HTTP {status})。',
        },
        lossless: {
            title: 'ブロックサイズ {chunk}（無損失）',
            body: '最小単位画像 {width}×{height} を基準に拡大・縮小するため、すべてのドットが正方形のまま残ります。',
        },
        cropOffer: {
            caption: '枠の内側だけが残ります',
            losslessTitle: '余白がブロック構造を隠しています',
            losslessBody:
                '背景を切り取るとブロックサイズ {chunk} が現れ、無損失リサイズが可能になります。' +
                '画像は {width}×{height} に切り取られます。',
            lossyTitle: 'ブロック構造が見つかりませんでした',
            lossyBody:
                '背景を切り取っても無損失リサイズはできません。ただし空の余白は取り除けます ' +
                '({fromWidth}×{fromHeight} → {toWidth}×{toHeight})。拡大は整数倍のニアレストネイバーになります。',
            acceptLossless: '背景を切り取って無損失に',
            acceptTrim: '余白だけ切り取る',
            decline: '元のサイズを保つ',
        },
        cropped: {
            losslessTitle: '背景を除去 · ブロックサイズ {chunk}（無損失）',
            losslessBody: '切り取った {width}×{height} の領域を基準に拡大・縮小します。',
            lossyTitle: '背景を除去 · ブロック構造なし',
            lossyBody:
                '切り取った {width}×{height} の領域を整数倍のニアレストネイバーでのみ拡大します。無損失ではありません。',
        },
        nearest: {
            title: 'ブロック構造が見つかりませんでした',
            body:
                '整数倍のニアレストネイバー拡大のみ可能で、無損失は保証されません。縮小は提供していません。',
            why: 'なぜこうなるのですか？',
        },
        controls: {
            scale: '倍率',
            resolution: '出力 {width} × {height}',
            adjusted: '{scale}× に補正',
            adjustedHint:
                'ドットあたりのピクセル数が整数になるよう倍率をスナップしました。これにより結果は無損失に保たれます。',
            mode: {
                slider: 'スライダー',
                custom: '倍率を入力',
            },
            customLabel: '倍率',
            losslessHint:
                'どんな倍率でも構いません。結果を無損失に保つため、ドットあたりのピクセル数が整数になるよう' +
                'スナップされます。そのため、適用倍率が入力値と少し異なることがあります。',
            lossyHint:
                'ブロック構造が見つからないため無損失にはできません。入力した倍率そのままでリサイズされ、' +
                'ドット格子は不揃いになります。',
            applied: '適用倍率 {scale}×',
            limit: 'この画像は最大 {max}× までです。それ以上はブラウザが結果を描画できません。',
            invalid: '0 より大きい数を入力してください。',
        },
        actions: {
            another: '別の画像を使う',
            download: 'PNG をダウンロード',
        },
        preview: {
            zoom: '実際の倍率',
            fit: '枠に合わせる',
            note: '実際の倍率では、プレビューが倍率に応じて拡大・縮小し、枠の外は切れて見えます。',
        },
    },

    how: {
        title: '仕組み',
        lead:
            'PixelZoom はドット絵を、独立したピクセルのビットマップではなく、誰かが拡大した小さなドットの' +
            '格子として扱います。このページの内容はすべて、この考え方から導かれます。',

        problem: {
            heading: 'ドット絵リサイズの問題',
            body:
                'バイリニアやバイキュービック補間はドット絵を定義する鋭い輪郭をぼかしてしまうため、通常は' +
                'ニアレストネイバーが使われます。しかしそれだけでは足りません。小数倍率ではドットごとに' +
                '個別に丸める必要があり、隣より 1 ピクセル広いドットが生じて格子の均一さが崩れます。' +
                'これがピクセルウォブル、あるいはシマリングと呼ばれる現象です。',
            sourceNote:
                '左が元の画像で、二つの結果がそこから枝分かれします。三つとも同じ倍率で描いているので、' +
                '下に見える大きさの違いがそのまま実際の違いです。ブロックはあえて小さくしてあります。' +
                '小さいほど損なわれ方がはっきり出るからです。',
            sourceLabel: '元の画像 · {sourceWidth}×{sourceHeight}',
            sourceCaption: '{chunk}×{chunk} のブロックで構成されています。ドット一つがちょうど {chunk} ピクセルです。',
            naiveLabel: '単純なニアレストネイバー {scale} 倍 · {naiveWidth}×{naiveHeight}',
            cleanLabel: 'PixelZoom {scale} 倍 · {cleanWidth}×{cleanHeight}',
            naiveCaption:
                'ドット幅: {widths} px。一つおきに隣より 1.5 倍広くなっています。両目の大きさと、' +
                '左右の輪郭の太さを見てください。',
            cleanCaption: 'ドット幅: {widths} px。どこでも同じです。',
            rulerNote:
                '各画像の上の帯は、あるドットが終わり次のドットが始まる位置を示します。色が交互に変わるので' +
                '幅を目で数えられます。',
            snapNote:
                'PixelZoom はちょうど {scale} 倍には拡大しません。ドットあたりのピクセル数を整数 {chunkOut} に' +
                '丸め、その結果は {effective} 倍になります。格子を保つには、要求された数値をそのまま守ることを' +
                'あきらめる必要があります。計算はステップ 3 で説明します。',
        },

        block: {
            heading: 'ステップ 1：固有ブロックサイズを見つける',
            body:
                '整数倍でのみ拡大されてきたドット絵は、同じ大きさの正方形ブロックで構成されています。' +
                'その大きさを n とします。PixelZoom は画像の幅と高さの公約数の中から、大きいものから順に n を探します。',
            testTitle: '候補 n ごとの検査',
            testBody:
                '画像を n で縮小し、ニアレストネイバーでそのまま戻してから元の画像と比較します。1 ピクセルも' +
                '違わなければ、すべての n×n ブロックが実際に単色だったということであり、n がブロックサイズです。' +
                '論文ではこの検査を通る画像を健全なドット絵と呼びます。',
            figureCaption:
                '検出されたブロックサイズ: {chunk}。格子がブロック境界を示しています。明るく強調した 1 マスが' +
                'ドット一つ、つまり単色の {chunk}×{chunk} ピクセルです。',
        },

        unit: {
            heading: 'ステップ 2：最小単位画像に還元する',
            body:
                '作品をブロックサイズで割ると最小単位画像が得られます。視覚上のドット一つが実際のピクセル一つに' +
                '正確に対応する形です。縮小の過程で失われるものはありません。そもそもすべてのブロックが' +
                '単色だったからです。これは拡大を取り除いた、作品の原データです。',
            figureCaption:
                '右の {unitWidth}×{unitHeight} の画像が、左の {artWidth}×{artHeight} の作品の内容すべてを' +
                '持っています。捨てられたものはありません。',
            scaleNote:
                '二つとも同じ {displayZoom} 倍で描いているので、ここに見える大きさの違いは実際の違いです。' +
                '縦横それぞれ {chunk} 分の 1 です。',
            sizeNote: '{artWidth}×{artHeight} ÷ {chunk} = {unitWidth}×{unitHeight}',
        },

        scale: {
            heading: 'ステップ 3：ドット単位でのみ拡大する',
            body:
                'ここまで来ればリサイズは単純です。PixelZoom は作品を拡大する代わりに、最小単位画像に' +
                'ドットあたりのピクセル数（整数）を掛けます。要求された倍率は最も近い整数を選ぶために使われるので、' +
                '出力は常にドット格子の正確な倍数になります。',
            formula: '出力 = 最小単位 × round(n × 要求倍率)',
            exampleTitle: '実際の計算',
            exampleBody:
                '上の作品はブロックサイズ {chunk}、最小単位 {unitWidth}×{unitHeight} です。{scale} 倍を要求すると ' +
                'round({chunk} × {scale}) = ドットあたり {chunkOut} ピクセルとなり、{outWidth}×{outHeight} が' +
                '得られます。すべてのドットがちょうど {chunkOut} ピクセルの正方形で、丸め誤差は蓄積しません。',
            sliderNote:
                '倍率スライダーが連続ではなく 1/n 刻みで動くのはこのためです。スライダーが到達できるすべての位置が、' +
                '格子の上に留まる倍率です。',
        },

        client: {
            heading: '処理が行われる場所',
            body:
                'サーバーは画像を見て、見つけたもの（ブロックサイズと内容の位置）だけを返します。拡大そのものは' +
                'お使いのブラウザのキャンバス上で行われます。二つの経路がビット単位で同一の結果を出すことを' +
                '数値で確認しているため、ローカルで処理しても失われるものはなく、画像がネットワークを' +
                '戻ってくることもありません。',
        },

        fallback: {
            heading: '検出に失敗したとき',
            body:
                'すべての画像が健全なドット絵とは限りません。PixelZoom は、健全なドット絵でない場合はそうお伝えします。' +
                '結果は順に三つです:',
            step1Title: 'そのまま検出',
            step1Body: '手を加えていない画像からブロックサイズが見つかりました。無損失リサイズで、何も変更しません。',
            step2Title: '切り取りの提案',
            step2Body:
                '余白はドット格子に揃っていないため、検出を妨げることがあります。取り除ける余白があれば、' +
                '何が切り取られるかを先にお見せしてから確認します。結果が無損失になるかどうかに関わらず同じです。',
            step3Title: 'ニアレストネイバーへのフォールバック',
            step3Body:
                'ブロックサイズが存在しない場合は整数倍のニアレストネイバー拡大を提供し、無損失ではないことを' +
                '明示します。',
            wobbleNote:
                '検出が失敗する最も多い理由は、その画像があなたの手元に届く前にすでに小数倍率でリサイズされて' +
                'いたことです。ドット幅がいったん混ざってしまうと、格子は取り戻せません。情報そのものが' +
                '失われているからです。',
        },

        paper: {
            heading: '厳密な定義',
            body:
                'ドット絵と健全なドット絵の数学的定義、最小単位検出の詳細な擬似コード、および評価指標' +
                '（色損失率、ブロックサイズ一貫性、可逆性）は、Applied Sciences に掲載された論文にあります。',
            cta: '論文を読む',
            repoCta: 'GitHub のコアアルゴリズム',
        },

        tryIt: '自分の画像で試す',
    },

    stats: {
        title: '統計と分析',
        lead:
            'サーバーに届いた画像は数を数え、それ以外は何も残しません。公開以来の累計です。',
        usersLabel: '使った人',
        usersNote: '一度でもアップロードしたブラウザの数',
        imagesLabel: '解析した画像',
        breakdownHeading: 'どんな画像だったか',
        lossless: 'そのまま処理できた',
        losslessNote: '手を加えずにブロックサイズが見つかりました。無損失リサイズです。',
        croppable: '背景を切れば無損失',
        croppableNote: '余白が格子を隠していました。背景を切り取ると現れます。',
        unsupported: '健全なドット絵ではない',
        unsupportedNote:
            'ブロックサイズが存在しません。たいていは、届く前にすでに小数倍率でリサイズされた画像です。',
        empty: 'まだ処理した画像はありません。',
        methodHeading: '何をどう数えるか',
        methodBody:
            'ブラウザは初めてアップロードするときに一度だけ数えられます。識別子はありません。' +
            'ブラウザが「これが最初です」と伝え、サーバーが受け取るのはその 1 ビットだけです。' +
            'ストレージを消したり別の端末から来れば再び数えられるので、人数の下限としてお読みください。',
        storedHeading: '何を残さないか',
        storedBody:
            '画像もファイル名もアドレスも識別子も残しません。記録は整数 1 行だけです：画像の枚数、' +
            '初回アップロードの回数、上の三つの区分ごとの枚数。',
        unavailable: '記録を読み込めませんでした。',
    },

    credits: {
        title: 'クレジット',
        lead:
            'PixelZoom は学部の HCI チーム設計課題から出発し、査読論文となり、このサービスへと育ちました。' +
            'その過程に関わった人たちです。',
        roles: {
            web: 'ウェブサービスの開発と配備',
            prototype: 'プロトタイプ',
            frontend: 'フロントエンド',
            backend: 'バックエンド',
            infra: 'インフラ設計への助言',
        },
        names: {
            seo: 'Jun Won Seo',
            leeJH: 'Jong Hyuck Lee',
            leeJW: 'Jun Won Lee',
            kim: 'Jun Beom Kim',
            jungYS: 'Yoon Sub Jung',
            jungJW: 'Jin-Woo Jung',
        },
        and: 'そして…',
        professorTitle: 'Professor {name}',
        professorNote:
            'この研究を可能にした研究費を確保してくださり、プロジェクトへの支援を惜しまれませんでした。',
        labTitle: 'HRILab',
        labNote: 'そして、そこで共に歩んだ研究室の仲間たち。',
        paperNote:
            'アルゴリズムは「Structure-Aware Pixel Art Scaling via Block Size Detection」' +
            '(Applied Sciences 16(5), 2026) として発表されています。',
    },

    support: {
        aria: 'Buy Me a Coffee で開発者を支援する（新しいタブで開きます）',
    },

    footer: {
        processed: 'PixelZoom は公開以来 {count} 枚のドット絵を処理しました。',
        privacy:
            'PixelZoom は個人情報を収集しません。アカウントもクッキーも解析ツールもありません。' +
            'アップロードされた画像はメモリ上で解析され、保存されることはありません。リサイズ自体は' +
            'お使いのブラウザで行われ、言語の選択もブラウザ内に留まります。残るのは通常のサーバー' +
            'アクセスログだけです。ただし集計値（何枚の画像を処理したか、いくつのブラウザが一度でも' +
            'アップロードしたか）は残します。整数であり、誰にも結び付いていません。',
        paper: '論文 (Applied Sciences)',
        coreRepo: 'コアアルゴリズム',
        tagline: '無損失ドット絵リサイズ',
    },
};

export default ja;
